import { type DBSchema, type IDBPDatabase, openDB } from "idb"

export interface QueuedTransaction {
  date: string
  amount: string
  type: "debit" | "credit"
  description: string
  owner: string
  categoryId?: string
  referenceNumber?: string
  notes?: string
  isTransfer?: boolean
  isIgnored?: boolean
}

interface QueueDB extends DBSchema {
  "pending-transactions": {
    key: string
    value: QueuedTransaction & { id: string }
  }
}

const DB_NAME = "paisa-offline"
const STORE = "pending-transactions"

let dbPromise: Promise<IDBPDatabase<QueueDB>> | null = null
function getDb() {
  if (!dbPromise) {
    dbPromise = openDB<QueueDB>(DB_NAME, 2, {
      upgrade(db, oldVersion) {
        // v1 used keyPath:"queuedAt" (number). v2 uses keyPath:"id" (UUID string).
        // Queue holds unsynced transient data — safe to drop and recreate.
        if (oldVersion < 2 && db.objectStoreNames.contains(STORE)) {
          db.deleteObjectStore(STORE)
        }
        db.createObjectStore(STORE, { keyPath: "id", autoIncrement: false })
      },
    })
  }
  return dbPromise
}

export async function enqueueTransaction(tx: QueuedTransaction): Promise<void> {
  const db = await getDb()
  await db.put(STORE, { ...tx, id: crypto.randomUUID() })
}

export async function queueCount(): Promise<number> {
  const db = await getDb()
  return db.count(STORE)
}

export async function flushQueue(send: (tx: QueuedTransaction) => Promise<void>): Promise<number> {
  const db = await getDb()
  const keys = await db.getAllKeys(STORE)
  let synced = 0
  for (const key of keys) {
    const item = await db.get(STORE, key)
    if (!item) continue
    const { id: _id, ...tx } = item
    try {
      await send(tx)
      await db.delete(STORE, key)
      synced++
    } catch {
      // keep item for next flush
    }
  }
  return synced
}

export async function clearQueue(): Promise<void> {
  const db = await getDb()
  await db.clear(STORE)
}
